import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PhoneVerificationModal from '../../components/PhoneVerificationModal';
import '../../styles/onboarding/Onboarding.css';
import OnboardingBottomNav from '../../components/common/OnboardingBottomNav';

const IdentityVerification = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    street: '',
    city: '',
    state: '',
    zipCode: ''
    // Removed phoneNumber since it will come from the modal
  });

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Form validation - phone verification is required to proceed
  const formIsValid = () => {
    const { firstName, lastName, dateOfBirth, street, city, state, zipCode } = formData;
    return firstName && lastName && dateOfBirth && street && city && state && zipCode && phoneVerified;
  };
  
  // Handle back button
  const handleBack = () => {
    navigate(`/group/${groupId}/onboarding`);
  };

  // Handle verify phone button
  const handleVerifyPhone = () => {
    setShowPhoneVerification(true);
  };

  // Handle phone verification completion - update form data with verified phone
  const handlePhoneVerificationComplete = (verifiedPhoneNumber) => {
    setPhoneVerified(true);
    // Update the form data with the verified phone number
    setFormData(prevData => ({
      ...prevData,
      phoneNumber: verifiedPhoneNumber
    }));
    setShowPhoneVerification(false);
  };

  // Handle next button
  const handleNext = () => {
    if (formIsValid()) {
      // Save form data to localStorage for use in final onboarding step
      localStorage.setItem('identityData', JSON.stringify(formData));
      console.log('Identity data saved to localStorage:', formData);
      navigate(`/group/${groupId}/onboarding/profile`);
    }
  };

  // Progress dots renderer
  const renderProgressDots = () => {
    const totalSteps = 4;
    const currentStep = 1;
    const dots = [];
    
    for (let i = 0; i < totalSteps; i++) {
      dots.push(
        <div 
          key={i} 
          className={`progress-dot ${i + 1 <= currentStep ? 'active' : ''}`}
        ></div>
      );
    }
    
    return dots;
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-content">
        <h1 className="onboarding-title">Verify your Identity</h1>
        <p className="onboarding-subtitle">
          Not everyone can join your community space. Help your Synagogue verify who you are.
        </p>
        
        {/* Identity verification form */}
        <div className="onboarding-form">
          {/* Full Name Section */}
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <div className="name-inputs">
              <input
                type="text"
                name="firstName"
                className="form-control"
                placeholder="First name"
                value={formData.firstName}
                onChange={handleChange}
              />
              <input
                type="text"
                name="lastName"
                className="form-control"
                placeholder="Last name"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Date of Birth */}
          <div className="form-group">
            <label className="form-label">Date of Birth</label>
            <input
              type="date"
              name="dateOfBirth"
              className="form-control"
              value={formData.dateOfBirth}
              onChange={handleChange}
              max={new Date().toISOString().split('T')[0]} // Set max date to today
            />
          </div>

          {/* Home Address */}
          <div className="form-group">
            <label className="form-label">Home Address</label>
            <div className="address-container">
              <input
                type="text"
                name="street"
                className="form-control"
                placeholder="Street"
                value={formData.street}
                onChange={handleChange}
              />
              <input
                type="text"
                name="city"
                className="form-control"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
              />
              <input
                type="text"
                name="state"
                className="form-control"
                placeholder="State"
                value={formData.state}
                onChange={handleChange}
              />
              <input
                type="text"
                name="zipCode"
                className="form-control"
                placeholder="Zip"
                value={formData.zipCode}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Phone Number Verification */}
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <div className="phone-verification-container">
              {!phoneVerified ? (
                <button 
                  type="button"
                  onClick={handleVerifyPhone}
                  className="verify-phone-btn primary-btn"
                >
                  Add Phone Number
                </button>
              ) : (
                <div className="phone-verified-status">
                  <span className="phone-verified-indicator">✓ Phone Number Verified</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Progress dots placed above bottom navigation */}
      <div className="fixed-progress-dots">
        {renderProgressDots()}
      </div>
      
      {/* Bottom Navigation */}
      <OnboardingBottomNav 
        onBack={handleBack} 
        onNext={handleNext} 
        nextDisabled={!formIsValid()} 
      />

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={showPhoneVerification}
        onClose={() => setShowPhoneVerification(false)}
        user={currentUser}
        onVerificationComplete={handlePhoneVerificationComplete}
      />
    </div>
  );
};

export default IdentityVerification;